// FILE 29: src/pages/Catalog.js
import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import { COLLECTIONS } from '../data/collections';
import CategoryFilter from '../components/catalog/CategoryFilter';
import ColorFilter from '../components/catalog/ColorFilter';
import ProductGrid from '../components/catalog/ProductGrid';
import ProductModal from '../components/catalog/ProductModal';
import { PRODUCTS } from '../data/products';
import './Catalog.css';

const SIZE_OPTIONS = [
  { key: 'all',   label: 'All Sizes' },
  { key: '8x48',  label: '8×48'  },
  { key: '12x24', label: '12×24' },
  { key: '24x48', label: '24×48' },
  { key: '48x72', label: '48×72' },
];

export default function Catalog() {
  const [searchParams, setSearchParams] = useSearchParams();
  const categoryParam = searchParams.get('category') || 'all';
  const sizeParam     = searchParams.get('size')     || 'all';
  const searchParam   = searchParams.get('search')   || '';

  const [activeCollection, setActiveCollection] = useState('all');
  const [activeCategory, setActiveCategory] = useState(categoryParam);
  const [activeSize,     setActiveSize]     = useState(sizeParam);
  const [activeColors,   setActiveColors]   = useState([]);
  const [searchValue,    setSearchValue]    = useState(searchParam);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const activeFilterCount = (activeCategory !== 'all' ? 1 : 0) + (activeSize !== 'all' ? 1 : 0) + activeColors.length;

  const handleCategorySelect = (key) => {
    setActiveCategory(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'all') next.delete('category');
      else next.set('category', key);
      return next;
    });
  };

  const handleSizeSelect = (key) => {
    setActiveSize(key);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (key === 'all') next.delete('size');
      else next.set('size', key);
      return next;
    });
  };

  const handleColorToggle = (colorKey) => {
    setActiveColors((prev) =>
      prev.includes(colorKey)
        ? prev.filter((c) => c !== colorKey)
        : [...prev, colorKey]
    );
  };

  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearchValue(val);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (!val) next.delete('search');
      else next.set('search', val);
      return next;
    });
  };

  const filtered = useMemo(() => {
    let list = PRODUCTS;

    // Collection filter
    if (activeCollection && activeCollection !== 'all') {
      list = list.filter((p) => p.collection === activeCollection);
    }

    // Category filter
    if (activeCategory && activeCategory !== 'all') {
      list = list.filter((p) => p.category === activeCategory);
    }

    // Size filter
    if (activeSize && activeSize !== 'all') {
      list = list.filter((p) =>
        p.sizes && p.sizes.some(
          (s) => s.toLowerCase().replace(/\s/g, '') === activeSize.toLowerCase()
        )
      );
    }

    // Search filter
    const q = searchValue.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.category.toLowerCase().includes(q) ||
          p.finish.toLowerCase().includes(q) ||
          (p.sizes && p.sizes.some(s => s.toLowerCase().includes(q))) ||
          p.description.toLowerCase().includes(q)
      );
    }

    // Color filter (multi-select — product must match at least one active colour)
    if (activeColors.length > 0) {
      list = list.filter(
        (p) => p.colors && p.colors.some((c) => activeColors.includes(c))
      );
    }

    return list;
  }, [activeCategory, activeSize, activeColors, searchValue]);

  return (
    <div className="catalog-page">
      <Helmet>
        <title>Tile Catalog | Porcelain &amp; Wood-Look Tile — Arden Surfaces</title>
        <meta name="description" content="Browse 37 premium porcelain and wood-look tiles in sizes 12×24 to 48×72. Matte, polished, and glossy finishes. Factory-direct pricing." />
        <meta property="og:title" content="Tile Catalog — Arden Surfaces" />
        <meta property="og:description" content="37 premium tiles including large-format porcelain slabs and wood-look planks. Dallas, TX showroom." />
        <meta property="og:url" content="https://www.ardensurfaces.com/catalog" />
      </Helmet>
      {/* Header banner */}
      <header className="catalog-header">
        <h1 className="catalog-header__title">Tile Catalog</h1>
        <p className="catalog-header__subtitle">
          {PRODUCTS.length} premium porcelain &amp; wood-look tiles
        </p>
      </header>

      <main className="catalog-main">
        {/* Search + mobile filter button row */}
        <div className="catalog-search-row">
          <div className="catalog-search">
            <input
              type="search"
              className="catalog-search__input"
              placeholder="Search by name, finish, or material…"
              value={searchValue}
              onChange={handleSearchChange}
              aria-label="Search tiles"
            />
            <span className="catalog-search__icon" aria-hidden="true">🔍</span>
          </div>
          <button className="catalog-filter-btn" onClick={() => setDrawerOpen(true)}>
            Filters {activeFilterCount > 0 && <span className="catalog-filter-btn__badge">{activeFilterCount}</span>}
          </button>
        </div>

        {/* Desktop filters */}
        <div className="catalog-filters-desktop">
          {/* Collection filter — top level */}
          <div className="catalog-collection-filter">
            <button
              className={`catalog-collection-pill${activeCollection === 'all' ? ' catalog-collection-pill--active' : ''}`}
              onClick={() => setActiveCollection('all')}
            >
              All Collections
            </button>
            {COLLECTIONS.map((c) => (
              <button
                key={c.key}
                className={`catalog-collection-pill${activeCollection === c.key ? ' catalog-collection-pill--active' : ''}`}
                onClick={() => setActiveCollection(c.key)}
              >
                {c.label}
              </button>
            ))}
          </div>
          {/* Active collection info strip */}
          {activeCollection !== 'all' && (() => {
            const col = COLLECTIONS.find(c => c.key === activeCollection);
            return col ? (
              <div className="catalog-collection-banner">
                <p className="catalog-collection-banner__label">{col.label} Collection</p>
                <p className="catalog-collection-banner__desc">{col.description}</p>
              </div>
            ) : null;
          })()}
          <CategoryFilter activeCategory={activeCategory} onSelect={handleCategorySelect} />
          <ColorFilter activeColors={activeColors} onToggle={handleColorToggle} />
          {activeColors.length > 0 && (
            <div className="catalog-active-filters">
              {activeColors.map((c) => (
                <span key={c} className="catalog-active-tag">
                  {c}
                  <button className="catalog-active-tag__remove" onClick={() => handleColorToggle(c)} aria-label={`Remove ${c} filter`}>×</button>
                </span>
              ))}
              <button className="catalog-clear-filters" onClick={() => setActiveColors([])}>Clear all</button>
            </div>
          )}
          <div className="catalog-size-filter">
            <span className="catalog-size-filter__label">Filter by size:</span>
            <div className="catalog-size-filter__pills">
              {SIZE_OPTIONS.map((opt) => (
                <button key={opt.key} className={`catalog-size-pill${activeSize === opt.key ? ' catalog-size-pill--active' : ''}`} onClick={() => handleSizeSelect(opt.key)}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Mobile filter drawer */}
        {drawerOpen && (
          <div className="catalog-drawer-backdrop" onClick={() => setDrawerOpen(false)}>
            <div className="catalog-drawer" onClick={e => e.stopPropagation()}>
              <div className="catalog-drawer__header">
                <span className="catalog-drawer__title">Filters</span>
                <button className="catalog-drawer__close" onClick={() => setDrawerOpen(false)}>&#x2715;</button>
              </div>
              <div className="catalog-drawer__body">
                <div className="catalog-collection-filter">
                  <button className={`catalog-collection-pill${activeCollection === 'all' ? ' catalog-collection-pill--active' : ''}`} onClick={() => setActiveCollection('all')}>All Collections</button>
                  {COLLECTIONS.map(c => (
                    <button key={c.key} className={`catalog-collection-pill${activeCollection === c.key ? ' catalog-collection-pill--active' : ''}`} onClick={() => setActiveCollection(c.key)}>{c.label}</button>
                  ))}
                </div>
                <CategoryFilter activeCategory={activeCategory} onSelect={handleCategorySelect} />
                <ColorFilter activeColors={activeColors} onToggle={handleColorToggle} />
                <div className="catalog-size-filter">
                  <span className="catalog-size-filter__label">Size</span>
                  <div className="catalog-size-filter__pills">
                    {SIZE_OPTIONS.map((opt) => (
                      <button key={opt.key} className={`catalog-size-pill${activeSize === opt.key ? ' catalog-size-pill--active' : ''}`} onClick={() => handleSizeSelect(opt.key)}>
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              <div className="catalog-drawer__footer">
                <button className="catalog-drawer__clear" onClick={() => { setActiveCategory('all'); setActiveSize('all'); setActiveColors([]); }}>Clear All</button>
                <button className="catalog-drawer__apply" onClick={() => setDrawerOpen(false)}>
                  Show {filtered.length} Results
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Result count */}
        <p className="catalog-count">
          Showing <strong>{filtered.length}</strong> of {PRODUCTS.length} tiles
          {activeSize !== 'all' && <span className="catalog-count__filter"> · {activeSize}" format</span>}
        </p>

        {/* Grid */}
        <ProductGrid
          products={filtered}
          onViewDetails={(product) => setSelectedProduct(product)}
        />
      </main>

      {/* Modal */}
      {selectedProduct && (
        <ProductModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </div>
  );
}
